/**
 * Pure helpers for ClemtexBrands. No SuiteCommerce dependencies, so every
 * function here is unit-tested in test/run-tests.js.
 */
define('Clemtex.Brands.Utils', ['underscore'], function (_) {
    'use strict';

    var ITEMS_API = /\/api\/(?:[a-z]+\/)?items(?:\?|$)/;
    var API_TEMPLATE_PARAMS = ['c', 'n', 'country', 'currency', 'language', 'pricelevel', 'use_pcv'];

    var Utils = {
        FACET_ID: 'custitem_web_manufacturer',

        // "Tinker & Rasor" -> "tinker-and-rasor"
        slugify: function slugify(label) {
            return String(label || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        },

        // Configuration rows -> { slug: brand }. Rows without a usable slug and
        // manufacturer value are dropped; a slug is taken as typed but normalised.
        // Rows with a Link URL are menu entries only (the URL already belongs to
        // something else, e.g. a product page), so they get no brand page.
        buildBrandMap: function buildBrandMap(rows, resolveAsset) {
            var map = {};
            _.each(rows || [], function (row) {
                var slug = Utils.slugify(row && row.slug);
                var facetValue = String((row && row.facetValue) || '').trim();
                if (!slug || !facetValue || map[slug] || String((row && row.linkUrl) || '').trim()) { return; }
                map[slug] = {
                    slug: slug,
                    facetValue: facetValue,
                    label: String(row.label || '').trim() || facetValue,
                    pageTitle: String(row.pageTitle || '').trim(),
                    metaDescription: String(row.metaDescription || '').trim(),
                    logo: Utils.resolveLogo(row.logoUrl, resolveAsset)
                };
            });
            return map;
        },

        // Configuration rows -> the brand menu: { featured: [...], all: [...] }.
        // featured = rows with a Featured Position, in that order; all = every
        // row A-Z by name. Each entry: { label, href, logo }. A logo value that
        // is not a URL is an extension asset path, resolved by resolveAsset.
        buildMenu: function buildMenu(rows, resolveAsset) {
            var seen = {};
            var entries = [];
            _.each(rows || [], function (row) {
                var slug = Utils.slugify(row && row.slug);
                var label = String((row && (row.label || row.facetValue)) || '').trim();
                if (!slug || !label || seen[slug]) { return; }
                seen[slug] = true;
                var link = String(row.linkUrl || '').trim();
                var logo = Utils.resolveLogo(row.logoUrl, resolveAsset);
                var position = parseInt(row.featuredPosition, 10);
                entries.push({
                    slug: slug,
                    label: label,
                    href: link || '/' + slug,
                    logo: logo,
                    position: isNaN(position) || position < 1 ? null : position
                });
            });
            var featured = _.sortBy(_.filter(entries, function (e) { return e.position !== null; }), 'position');
            var all = _.sortBy(entries, function (e) { return e.label.toLowerCase(); });
            return { featured: featured, all: all };
        },

        // A logo value that is not a URL is an extension asset path.
        resolveLogo: function resolveLogo(value, resolveAsset) {
            var logo = String(value || '').trim();
            if (logo && !/^(https?:)?\/\//.test(logo) && logo.charAt(0) !== '/' && resolveAsset) {
                logo = resolveAsset(logo);
            }
            return logo;
        },

        // Splits a list into n columns of near-equal length, filled top to bottom.
        columns: function columns(list, n) {
            var size = Math.ceil((list || []).length / Math.max(1, n)) || 1;
            var out = [];
            for (var i = 0; i < (list || []).length; i += size) { out.push(list.slice(i, i + size)); }
            return out;
        },

        // "clemco/blast/equipment?order=x" -> { brand, categoryUrl: "/blast/equipment", query: "order=x" }
        parseFragment: function parseFragment(fragment, brandMap) {
            var clean = String(fragment || '').replace(/^\/+/, '');
            var queryIndex = clean.indexOf('?');
            var pathPart = queryIndex < 0 ? clean : clean.slice(0, queryIndex);
            var query = queryIndex < 0 ? '' : clean.slice(queryIndex + 1);
            var segments = _.compact(pathPart.split('/'));
            var brand = brandMap && brandMap[String(segments[0] || '').toLowerCase()];
            if (!brand) { return null; }
            return {
                brand: brand,
                categoryUrl: segments.length > 1 ? '/' + segments.slice(1).join('/') : null,
                query: query
            };
        },

        brandUrl: function brandUrl(brand, categoryUrl) {
            return '/' + brand.slug + (categoryUrl || '');
        },

        // Post-process a core translator URL so it stays under /<slug> and does
        // not repeat the manufacturer facet the path already implies.
        //   "/clemco?custitem_web_manufacturer=Clemco&order=x" -> "/clemco?order=x"
        //   "/blast/equipment?custitem_material=Brass"          -> "/clemco/blast/equipment?custitem_material=Brass"
        rewriteUrl: function rewriteUrl(url, brand, facetId) {
            if (!url || url === '#') { return url; }
            var parts = String(url).split('?');
            var base = parts[0].charAt(0) === '/' ? parts[0] : '/' + parts[0];
            var prefix = '/' + brand.slug;
            if (base !== prefix && base.indexOf(prefix + '/') !== 0) { base = prefix + base; }
            var kept = _.filter((parts[1] || '').split('&'), function (pair) {
                return pair && pair.split('=')[0] !== (facetId || Utils.FACET_ID);
            });
            return kept.length ? base + '?' + kept.join('&') : base;
        },

        // ---- category tree -------------------------------------------------
        findNode: function findNode(nodes, fullurl) {
            for (var i = 0; i < (nodes || []).length; i++) {
                if (nodes[i].fullurl === fullurl) { return nodes[i]; }
                var hit = Utils.findNode(nodes[i].categories, fullurl);
                if (hit) { return hit; }
            }
            return null;
        },

        // Ancestors-and-self of a category, top down; null when not found.
        chainTo: function chainTo(nodes, fullurl, trail) {
            for (var i = 0; i < (nodes || []).length; i++) {
                var next = (trail || []).concat([nodes[i]]);
                if (nodes[i].fullurl === fullurl) { return next; }
                var hit = Utils.chainTo(nodes[i].categories, fullurl, next);
                if (hit) { return hit; }
            }
            return null;
        },

        // ---- tiles ---------------------------------------------------------
        // The page's own response lists the NAMES of every category holding the
        // brand's items (facet "commercecategoryname"). Used to skip categories
        // that cannot match before spending a count request on them.
        namesFromFacets: function namesFromFacets(facets) {
            var facet = _.find(facets || [], function (f) { return f && f.id === 'commercecategoryname'; });
            var names = {};
            _.each((facet && facet.values) || [], function (v) {
                var name = String((v && (v.label || v.url)) || '').toLowerCase();
                if (name) { names[name] = true; }
            });
            return names;
        },

        parseExcludeList: function parseExcludeList(value) {
            return _.compact(_.map(String(value || '').split(','), function (s) {
                s = s.trim();
                if (!s) { return null; }
                return s.charAt(0) === '/' ? s : '/' + s;
            }));
        },

        candidateChildren: function candidateChildren(children, names, excludeUrls) {
            return _.filter(children || [], function (child) {
                if (_.contains(excludeUrls || [], child.fullurl)) { return false; }
                // no names list (facet missing) -> let the count decide
                return _.isEmpty(names) || !!names[String(child.name || '').toLowerCase()];
            });
        },

        selectTiles: function selectTiles(candidates, counts, minItems) {
            var tiles = _.map(candidates, function (node, index) { return { node: node, count: counts[index] || 0 }; });
            tiles = _.filter(tiles, function (tile) { return tile.count >= (minItems || 1); });
            return _.sortBy(tiles, function (tile) { return parseFloat(tile.node.sequencenumber) || 0; });
        },

        // Works out which tiles to show for a scope (null = brand home).
        //   opts: { tree, scopeUrl, names, excludeUrls, minItems, skipSingle,
        //           countMany(nodes, cb(countsArray)) }
        // With skipSingle, a level that yields exactly one tile is replaced by
        // that category's own children, repeatedly, so a brand that lives in a
        // single branch (Kennametal -> Blast -> ...) opens on a useful choice.
        // If that walk never reaches a real choice (it ends on one tile, e.g. a
        // brand whose only categorised items sit in one leaf), no tiles are
        // shown: a lone tile is not navigation. With skipSingle off, levels are
        // shown exactly as they are.
        resolveTiles: function resolveTiles(opts, done) {
            var scopeNode = opts.scopeUrl ? Utils.findNode(opts.tree, opts.scopeUrl) : null;
            var firstLevel = opts.scopeUrl ? ((scopeNode && scopeNode.categories) || []) : (opts.tree || []);
            var guard = 0;

            function finish(tiles) {
                done(opts.skipSingle && tiles.length < 2 ? [] : tiles);
            }

            (function step(children) {
                var candidates = Utils.candidateChildren(children, opts.names, opts.excludeUrls);
                if (!candidates.length || guard++ > 8) { return finish([]); }
                opts.countMany(candidates, function (counts) {
                    var tiles = Utils.selectTiles(candidates, counts, opts.minItems);
                    var only = tiles.length === 1 ? tiles[0] : null;
                    if (opts.skipSingle && only && (only.node.categories || []).length) {
                        return step(only.node.categories);
                    }
                    finish(tiles);
                });
            })(firstLevel);
        },

        // ---- titles ----------------------------------------------------------
        pageTitle: function pageTitle(brand, chain, siteName) {
            var suffix = siteName ? ' | ' + siteName : '';
            var last = _.last(chain || []);
            if (!last && brand.pageTitle) { return brand.pageTitle; }
            var name = last ? last.name : 'Products';
            // category names that already carry the brand ("Clemco Inlet Valve Diagram")
            var prefixed = name.toLowerCase().indexOf(brand.label.toLowerCase()) !== -1;
            return (prefixed ? name : brand.label + ' ' + name) + suffix;
        },

        // ---- Item Search API -----------------------------------------------
        isItemsApi: function isItemsApi(url) { return ITEMS_API.test(String(url || '')); },

        // Learns base URL + site parameters from one of the core's own calls.
        parseApiTemplate: function parseApiTemplate(url) {
            var parts = String(url || '').split('?');
            if (!parts[0]) { return null; }
            var params = {};
            _.each((parts[1] || '').split('&'), function (pair) {
                var index = pair.indexOf('=');
                if (index > 0) { params[pair.slice(0, index)] = pair.slice(index + 1); }   // kept encoded
            });
            return { base: parts[0], params: _.pick(params, API_TEMPLATE_PARAMS) };
        },

        // URL that returns `total` = the brand's items inside one category.
        countUrl: function countUrl(template, brand, categoryUrl, facetId) {
            var params = _.extend({}, template.params, { fieldset: 'typeahead', limit: '1', offset: '0' });
            params.commercecategoryurl = encodeURIComponent(categoryUrl);
            params[facetId || Utils.FACET_ID] = encodeURIComponent(brand.facetValue);
            return template.base + '?' + _.map(_.keys(params).sort(), function (key) { return key + '=' + params[key]; }).join('&');
        },

        // ---- brand home --------------------------------------------------------
        // Automatic cards for a brand with no Brand Page record: the second-level
        // categories (Blast > Blast Equipment, Parts...) the brand has items in,
        // since the top level (Blast, Other, Diagrams) says too little. A top-level
        // category with no children stands in for itself.
        homeCandidates: function homeCandidates(tree, names, excludeUrls) {
            var out = [];
            _.each(Utils.candidateChildren(tree, names, excludeUrls), function (top) {
                var children = Utils.candidateChildren(top.categories, names, excludeUrls);
                if (children.length) { out.push.apply(out, children); } else if (!(top.categories || []).length) { out.push(top); }
            });
            return out;
        },

        // The busiest qualifying categories first, at most `max` of them.
        pickHomeCards: function pickHomeCards(candidates, counts, minItems, max) {
            var cards = _.map(candidates, function (node, index) { return { node: node, count: counts[index] || 0, index: index }; });
            cards = _.filter(cards, function (card) { return card.count >= (minItems || 1); });
            cards = _.sortBy(cards, function (card) { return card.index; });
            cards = _.sortBy(cards, function (card) { return -card.count; });
            return cards.slice(0, max || 8);
        },

        // Intro text -> paragraphs: a blank line starts a new one, single line
        // breaks inside a paragraph become spaces.
        paragraphs: function paragraphs(text) {
            return _.compact(_.map(String(text || '').split(/\r?\n\s*\r?\n/), function (p) {
                return p.replace(/\s*\r?\n\s*/g, ' ').trim();
            }));
        },

        countLabel: function countLabel(count) {
            return count + (count === 1 ? ' product' : ' products');
        },

        // Route patterns equivalent to the core's Facets.Router.addUrl().
        routePatterns: function routePatterns(slugs) {
            var group = _.map(slugs, function (s) { return s.replace(/[^a-z0-9-]/g, ''); }).join('|');
            return [new RegExp('^\\b(' + group + ')\\b$'), new RegExp('^\\b(' + group + ')\\b[\\/\\?].*$')];
        }
    };

    return Utils;
});
